import * as React from "react";
import {
  Button,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
} from "@fluentui/react-components";
import { useStylesTable } from "../../styles/TableStyle";
import { TableProps } from "../../interfaces/TableProps";
import { columnSizingOptionsUsers, getColumnsUsers } from "./TableUsersColumns";
import ClipLoader from "react-spinners/ClipLoader";
import { ThemeContext } from "../../utils/GlobalContexts";
import { Microsoft_Graph_Models_User } from "../../CustomerSvcService";
import { TABLE } from "../../utils/Enums";
import {
  columnSizingOptionsCompanyData,
  getColumnsCompanyData as getColumnsUserManagerCompanyData,
} from "./TableUserManagerCompanyDataColumns";
import {
  columnSizingOptionsAdatpontTenants,
  getColumnsAdatpontTenants,
} from "./TableAdatPontTenantsColumns";
import {
  columnSizingOptionsUserManagerUsers,
  getColumnsUserManagerUsers,
} from "./TableUserManagerUsersColumns";
import { useNavigate } from "react-router-dom";
import {
  columnSizingOptionsAdatpontCompanies,
  getColumnsAdatpontCompanies,
} from "./TableAdatPontCompaniesColumns";
import {
  columnSizingOptionsAdatpontInvitations,
  getColumnsAdatpontInvitations,
} from "./TableAdatPontInvitationsColumns";
import {
  columnSizingOptionsAdatpontUsers,
  getColumnsAdatpontUsers,
} from "./TableAdatPontUsersColumns";
import {
  columnSizingOptionsTransactions,
  getColumnsTransactions,
} from "./TableTransactionsColumns";
import { getColumnsTransaction } from "./TableTransactionColumns";
import {
  columnSizingOptionsMerchants,
  getColumnsMerchants,
} from "./TableMerchantsColumns";
import {
  columnSizingOptionsRenewalErrors,
  getColumnsRenewalErrors,
} from "./TableRenewalErrorsColumns";
import { getColumnsCoupon } from "./TableCouponsColumns";
import { getColumnsUsedCoupons } from "./TableUsedCouponsColumns";
import {
  columnSizingOptionsApprenticeWizards,
  getColumnsApprenticeWizards,
} from "./TableApprenticeWizardsColumns";
import {
  getColumnsForAccountedCompanies,
  tableColsSizes as columnSizingOptionsConnectAccountedCompanies,
} from "./table-kulcs-connect/TableAccountedCompanies";
import {
  getColumnsForSubscriptionData,
  tableColsSizes as columnSizingOptionsConnectSubscriptionData,
} from "./table-kulcs-connect/TableSubscriptionData";
import {
  getColumnsForM2M,
  tableColsSizes as columnSizingOptionsM2M,
} from "./m2m-admin/TableM2M";

const TableComponent: React.FC<TableProps> = ({
  data,
  selectedDto,
  setSelectedDto,
  isLoading,
  isLoadingMoreData,
  showMore,
  nextLink,
  handleRowAction,
  unDeleteUser,
  type,
  oid,
  pageSize: pageSizeProp,
  currentPage: currentPageProp,
  onPageChange,
  isServerSidePagination = false,
}) => {
  const { theme } = React.useContext(ThemeContext);
  const styles = useStylesTable();
  const [internalPage, setInternalPage] = React.useState(1);
  const [pageSizeState] = React.useState(20);
  const navigate = useNavigate();

  const columns =
    (type === TABLE.USERS
      ? getColumnsUsers(theme, handleRowAction, navigate)
      : type === TABLE.USERMANAGER_COMPANYDATA
        ? getColumnsUserManagerCompanyData(theme, handleRowAction, unDeleteUser)
        : type === TABLE.ADATPONT_TENANTS
          ? getColumnsAdatpontTenants(theme, navigate, oid ?? "")
          : type === TABLE.ADATPONT_INVITATIONS
            ? getColumnsAdatpontInvitations(theme)
            : type === TABLE.ADATPONT_USERS
              ? getColumnsAdatpontUsers(theme)
              : type === TABLE.ADATPONT_COMPANIES
                ? getColumnsAdatpontCompanies(theme)
                : type === TABLE.TRANSACTIONS
                  ? getColumnsTransactions(theme)
                  : type === TABLE.TRANSACTION
                    ? getColumnsTransaction(theme)
                    : type === TABLE.MERCHANTS
                      ? getColumnsMerchants(theme, handleRowAction, navigate)
                      : type === TABLE.COUPONS
                        ? getColumnsCoupon(theme, navigate, handleRowAction)
                        : type === TABLE.RENEWAL_ERRORS
                          ? getColumnsRenewalErrors(theme)
                          : type === TABLE.USEDCOUPONS
                            ? getColumnsUsedCoupons(theme, navigate)
                            : type === TABLE.APPRENTICE_WIZARDS
                              ? getColumnsApprenticeWizards(theme)
                              : type === TABLE.CONNECT_ACCOUNTED_COMPANIES
                                ? getColumnsForAccountedCompanies(
                                    theme,
                                    handleRowAction ??
                                      ((
                                        actionOrOid: string | null | undefined,
                                        item?: any,
                                      ) => {}),
                                  )
                                : type === TABLE.CONNECT_SUBSCRIPTION_DATA
                                  ? getColumnsForSubscriptionData(theme)
                                  : type === TABLE.M2M
                                    ? getColumnsForM2M(theme)
                                    : getColumnsUserManagerUsers(theme)) ?? [];

  const effectivePageSize = pageSizeProp ?? pageSizeState;
  const pageCount = Math.max(
    1,
    Math.ceil((data?.length ?? 0) / effectivePageSize),
  );

  React.useEffect(() => {
    if (currentPageProp === undefined) setInternalPage(1);
  }, [effectivePageSize, data, currentPageProp]);

  const computedColumnSizingOptions = React.useMemo(() => {
    if (!columns || columns.length === 0) return undefined;
    const sizing: Record<string, { minWidth: number; defaultWidth: number }> =
      {};

    let ctx: CanvasRenderingContext2D | null = null;
    try {
      const c = document.createElement("canvas");
      ctx = c.getContext("2d");
      ctx!.font = "14px 'Segoe UI', Roboto, Arial, sans-serif";
    } catch (e) {
      ctx = null;
    }

    const padding = 32;
    const minWidth = 48;
    const maxWidth = 900;

    const sampleRows = (data ?? []).slice(0, 50);

    for (let colIndex = 0; colIndex < (columns as any[]).length; colIndex++) {
      const col = (columns as any[])[colIndex];
      const id = String(col.columnId ?? "");

      let headerText = id;
      try {
        const rh = col.renderHeaderCell && col.renderHeaderCell();
        headerText = typeof rh === "string" ? rh : String(rh ?? id);
      } catch (_e) {
        headerText = id;
      }

      const headerW = ctx
        ? Math.ceil(ctx.measureText(headerText).width)
        : headerText.length * 8;

      let maxCellW = 0;
      for (const row of sampleRows) {
        try {
          const raw = (row as any)[id];
          if (raw === null || raw === undefined) continue;
          const txt = typeof raw === "string" ? raw : String(raw);
          const w = ctx
            ? Math.ceil(ctx.measureText(txt).width)
            : txt.length * 8;
          if (w > maxCellW) maxCellW = w;
        } catch (_e) {}
      }

      const contentW = Math.max(headerW, maxCellW);
      const leadColumnBoost = colIndex < 3 ? 120 : 0;
      const defaultWidth = Math.min(
        maxWidth,
        Math.max(minWidth, contentW + padding + leadColumnBoost),
      );
      sizing[id] = {
        minWidth: Math.max(36, Math.round(defaultWidth * 0.6)),
        defaultWidth: Math.round(defaultWidth),
      };
    }

    return sizing;
  }, [data, columns]);

  const handleRowClick = (e: any, item: any) => {
    if (type !== TABLE.USERS) return;
    setSelectedDto && setSelectedDto(item as Microsoft_Graph_Models_User);
  };

  const refMap = React.useRef<Record<string, HTMLElement | null>>({});

  const getRowColor = (item: any) => {
    if (type !== TABLE.USERS) return styles.row;
    if (selectedDto?.id === (item as Microsoft_Graph_Models_User).id)
      return styles.selectedRow;
    else return styles.row;
  };

  const usedPage = currentPageProp ?? internalPage;

  const isServerSidePaging =
    type === TABLE.TRANSACTIONS ||
    type === TABLE.TRANSACTION ||
    type === TABLE.APPRENTICE_WIZARDS ||
    isServerSidePagination;

  function getPageData() {
    if (isServerSidePaging) {
      return data;
    }

    const startIndex = (usedPage - 1) * effectivePageSize;
    const endIndex = startIndex + effectivePageSize;
    return data?.slice(startIndex, endIndex);
  }

  const goToPage = (newPage: number) => {
    if (onPageChange) onPageChange(newPage);
    else if (!isServerSidePaging) setInternalPage(newPage);
  };

  return isLoading ? (
    <div style={{ textAlign: "center", margin: "20px" }}>
      <ClipLoader size={60} color={theme.colorBrandForeground1} />
    </div>
  ) : data?.length === 0 || !data ? (
    <div style={{ textAlign: "center", margin: "20px" }}>
      <div>Nincsenek megjeleníthető adatok</div>
    </div>
  ) : (
    <div>
      <DataGrid
        size="small"
        columns={columns}
        items={getPageData() ?? []}
        sortable
        resizableColumns
        getRowId={(item: any) => item.rowKey ?? item.transactionId ?? item.id}
        style={{ borderCollapse: "collapse" }}
        columnSizingOptions={
          computedColumnSizingOptions ??
          (type === TABLE.USERS
            ? columnSizingOptionsUsers
            : type === TABLE.USERMANAGER_COMPANYDATA
              ? columnSizingOptionsCompanyData
              : type === TABLE.ADATPONT_TENANTS
                ? columnSizingOptionsAdatpontTenants
                : type === TABLE.ADATPONT_COMPANIES
                  ? columnSizingOptionsAdatpontCompanies
                  : type === TABLE.ADATPONT_INVITATIONS
                    ? columnSizingOptionsAdatpontInvitations
                    : type === TABLE.ADATPONT_USERS
                      ? columnSizingOptionsAdatpontUsers
                      : type === TABLE.TRANSACTIONS
                        ? columnSizingOptionsTransactions
                        : type === TABLE.MERCHANTS
                          ? columnSizingOptionsMerchants
                          : type === TABLE.RENEWAL_ERRORS
                            ? columnSizingOptionsRenewalErrors
                            : type === TABLE.APPRENTICE_WIZARDS
                              ? columnSizingOptionsApprenticeWizards
                              : type === TABLE.CONNECT_ACCOUNTED_COMPANIES
                                ? columnSizingOptionsConnectAccountedCompanies
                                : type === TABLE.CONNECT_SUBSCRIPTION_DATA
                                  ? columnSizingOptionsConnectSubscriptionData
                                  : type === TABLE.M2M
                                    ? columnSizingOptionsM2M
                                    : columnSizingOptionsUserManagerUsers)
        }
      >
        <DataGridHeader
          style={{ position: "sticky", top: "0px", zIndex: 1000 }}
        >
          <DataGridRow
            id="table-header"
            className={styles.tableHeader}
            style={{ backgroundColor: theme.colorBrandForeground1 }}
          >
            {({ renderHeaderCell, columnId }, dataGrid) =>
              dataGrid.resizableColumns ? (
                <DataGridHeaderCell
                  ref={(el) => (refMap.current[columnId] = el)}
                  className={styles.headerCell}
                >
                  {renderHeaderCell()}
                </DataGridHeaderCell>
              ) : (
                <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
              )
            }
          </DataGridRow>
        </DataGridHeader>

        <DataGridBody>
          {({ item, rowId }) => (
            <DataGridRow
              key={rowId}
              className={getRowColor(item)}
              onClick={(e: any) => handleRowClick(e, item as any)}
            >
              {({ renderCell }) => (
                <DataGridCell className={styles.bodyCell}>
                  {renderCell(item)}
                </DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
        }}
      >
        <div>
          {isLoadingMoreData ? (
            <ClipLoader size={28} color={theme.colorBrandForeground1} />
          ) : nextLink ? (
            <span
              style={{
                cursor: "pointer",
                color: theme.colorBrandForeground1,
                fontWeight: 700,
              }}
              onClick={showMore}
            >
              Több betöltése
            </span>
          ) : null}
        </div>

        {/* internal pagination shown only when parent doesn't control pagination 
            ÉS NEM Szerver Oldali Lapozást használunk.
        */}
        {onPageChange || isServerSidePaging ? null : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Button
              disabled={usedPage <= 1}
              onClick={() => goToPage(Math.max(1, usedPage - 1))}
            >
              Előző
            </Button>
            <span
              style={{ fontSize: 13 }}
            >{`Oldal ${usedPage} / ${pageCount}`}</span>
            <Button
              disabled={usedPage >= pageCount}
              onClick={() => goToPage(Math.min(pageCount, usedPage + 1))}
            >
              Következő
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableComponent;
